import { execAndWaitForAll } from "../../Helpers/Functions/promise"
import { selectUserName } from "../../Helpers/Functions/Selectors/auth"
import { APIAppWorkerGroup } from "../../Models/API/AppWorkerGroup"
import { AppWorkerRole } from "../../Models/API/AppWorkerRole"
import { APIWorkerGroup } from "../../Models/API/WorkerGroup"
import { hasMore } from "../../Models/Client/RemoteData"
import { SortExpression } from "../../Models/Client/SortExpression"
import { MainThunkDispatch, State } from "../../Reducers"
import { apiEndpoints } from "../../Routes/api"
import { failMessage, successMessage } from "../Client/MessagesActions"
import { apiFetch } from "../Client/NetworkActions"
import { WorkerGroupType } from "../Types"
import { reloadWorkers } from "./WorkerActions"

export const createWorkerGroup =
    (orgUsername: string, name: string, workerIds: number[] = []) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.createWorkerGroup({
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "POST",
                    body: JSON.stringify({ name, workerIds })
                })
            )

            dispatch(successMessage("Worker group is created"))
            dispatch(reloadWorkerGroups(orgUsername))
        } catch (error) {
            dispatch(failMessage("Could not create worker group", error))
            throw error
        }
    }

export const removeWorkerGroup =
    (orgUsername: string, id: number) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.removeWorkerGroup({
                id,
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "DELETE"
                })
            )

            dispatch(successMessage("Worker group is removed"))
            dispatch(reloadWorkerGroups(orgUsername))
        } catch (error) {
            dispatch(failMessage("Could not remove worker group", error))
            throw error
        }
    }

export const listWorkerGroups =
    (orgUsername: string, page: number, sort: SortExpression<APIWorkerGroup>) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.getWorkerGroupList({
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername,
                page,
                pageSize: 30,
                ...sort
            })
            const data = await dispatch(
                apiFetch<{ content: APIWorkerGroup[]; total: number }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                return
            }

            dispatch({
                type: WorkerGroupType.setList,
                meta: {
                    orgUsername,
                    page,
                    sort,
                    total: data.total
                },
                payload: data.content
            })
        } catch (error) {}
    }

export const reloadWorkerGroups =
    (orgUsername: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workerGroups.org[orgUsername]

        if (entry?.list === undefined) {
            return
        }

        await dispatch(listWorkerGroups(orgUsername, 0, entry.list.params))
    }

export const loadMoreWorkerGroups =
    (orgUsername: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workerGroups.org[orgUsername]

        if (entry?.list === undefined || !hasMore(entry)) {
            return
        }

        await dispatch(
            listWorkerGroups(
                orgUsername,
                entry.list.pages.length,
                entry.list.params
            )
        )
    }

export const updateWorkerGroup =
    (orgUsername: string, group: APIWorkerGroup) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.patchWorkerGroup({
                id: group.id,
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "PATCH",
                    body: JSON.stringify({
                        name: group.name,
                        workerIds: group.workers.map(worker => worker.id)
                    })
                })
            )

            dispatch(successMessage("Worker group is updated"))
            dispatch(reloadWorkerGroups(orgUsername))
            dispatch(reloadWorkers(orgUsername))
        } catch (error) {
            dispatch(failMessage("Could not update worker group", error))
            throw error
        }
    }

export const listAppWorkerGroups =
    (appID: string, page: number, sort: SortExpression<APIAppWorkerGroup>) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.getAppWorkerGroupList({
                appID,
                page,
                pageSize: 30,
                ...sort
            })
            const data = await dispatch(
                apiFetch<{ content: APIAppWorkerGroup[]; total: number }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                return
            }

            dispatch({
                type: WorkerGroupType.setAppWorkerGroupList,
                meta: {
                    appID,
                    page,
                    sort,
                    total: data.total
                },
                payload: data.content
            })
        } catch (error) {}
    }

export const reloadAppWorkerGroups =
    (appID: string) => (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workerGroups.app[appID]

        if (entry?.list === undefined) {
            return
        }

        dispatch(listAppWorkerGroups(appID, 0, entry.list.params))
    }

export const loadMoreAppWorkerGroups =
    (appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workerGroups.app[appID]

        if (entry?.list === undefined || !hasMore(entry)) {
            return
        }

        await dispatch(
            listAppWorkerGroups(
                appID,
                entry.list.pages.length,
                entry.list.params
            )
        )
    }

export const assignWorkerGroupToApp =
    (appID: string, ids: number[], role: AppWorkerRole) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            await execAndWaitForAll(
                ids.map(id => {
                    const endpoint = apiEndpoints.assignWorkerGroupToApp({
                        appID,
                        id
                    })

                    return dispatch(
                        apiFetch(endpoint.url, endpoint, {
                            method: "POST",
                            body: JSON.stringify({ role })
                        })
                    )
                })
            )

            dispatch(
                successMessage(
                    ids.length > 1
                        ? "Worker groups are added to project"
                        : "Worker group is added to project"
                )
            )
            dispatch(reloadAppWorkerGroups(appID))
        } catch (error) {
            dispatch(
                failMessage("Could not add worker group to project", error)
            )
            throw error
        }
    }

export const unassignWorkerGroupFromApp =
    (appID: string, id: number) => async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.unassignWorkerGroupFromApp({
                id,
                appID
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "DELETE"
                })
            )

            dispatch(successMessage("Worker group is removed"))
            dispatch(reloadAppWorkerGroups(appID))
        } catch (error) {
            dispatch(failMessage("Could not remove worker group", error))
            throw error
        }
    }

export const updateWorkerGroupInApp =
    (worker: APIAppWorkerGroup) => async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.patchWorkerGroupInApp({
                id: worker.workerGroupId,
                appID: worker.appUuid
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "PATCH",
                    body: JSON.stringify({ role: worker.role })
                })
            )

            dispatch(successMessage("Worker group is updated"))
            dispatch(reloadAppWorkerGroups(worker.appUuid))
        } catch (error) {
            dispatch(failMessage("Could not update worker group", error))
            throw error
        }
    }
